import { useState, useEffect } from "react";
import { Box, Chip, Stack } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router";
import { request } from "../../config/request.ts";
import DataTable, { Column } from "../../shared/DataTable/index.tsx";
import PageHeader from "../../shared/PageHeader/index.tsx";
import DeleteConfirmation from "../../shared/DeleteConfirmation/index.tsx";
import StatusChip from "../../shared/StatusChip/index.tsx";
import ActionIcons from "../../shared/ActionIcons/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";

interface AchievementItem {
  id: number;
  title: string;
  category: string;
  date: string;
  images: { path: string; description: string | null }[];
  tags: string[];
  sort_order: number;
  status: boolean;
}

export default function Achievements() {
  const navigate = useNavigate();
  const [achievements, setAchievements] = useState<AchievementItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getAchievements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, search]);

  const getAchievements = async () => {
    setLoading(true);
    try {
      const response = await request.post("achievements", {
        search: search,
        limit,
        offset: page,
        orderColumn: "sort_order",
        orderDirection: "desc",
      });
      setAchievements(response.data.data);
      setTotalCount(response.data.total);
    } finally {
      setLoading(false);
    }
  };

  const handlePaginationChange = (newPage: number, newLimit: number) => {
    setPage(newPage);
    setLimit(newLimit);
  };

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: 0,
    title: null as string | null,
  });

  const columns: Column[] = [
    {
      id: "slNo",
      label: "Sl.No",
      minWidth: 60,
      format: (_value, _row, index) => (
        <Box component="span" sx={{ fontWeight: 600, color: "#64748b" }}>
          {page * limit + index + 1}
        </Box>
      ),
    },
    {
      id: "images",
      label: "Image",
      minWidth: 80,
      format: (_value, row) => (
        <Box
          component="img"
          src={row.images?.[0]?.path}
          sx={{
            width: 48,
            height: 48,
            objectFit: "cover",
            borderRadius: "8px",
          }}
        />
      ),
    },
    {
      id: "title",
      label: "Title",
      minWidth: 220,
      format: (value) => value,
    },
    {
      id: "description",
      label: "Description",
      minWidth: 240,
      format: (value) =>
        value
          ? `${value.slice(0, 80)}${value.length > 80 ? "…" : ""}`
          : "—",
    },
    {
      id: "category",
      label: "Category",
      minWidth: 140,
      format: (value) => value || "—",
    },
    {
      id: "date",
      label: "Date",
      minWidth: 130,
      format: (value) => value || "—",
    },
    // {
    //   id: "tags",
    //   label: "Tags",
    //   minWidth: 200,
    //   format: (value: string[]) => (
    //     <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
    //       {(value || []).slice(0, 2).map((tag) => (
    //         <Chip key={tag} label={tag} size="small" />
    //       ))}
    //       {(value || []).length > 2 && (
    //         <Chip label={`+${value.length - 2}`} size="small" color="primary" />
    //       )}
    //     </Stack>
    //   ),
    // },
    {
      id: "sort_order",
      label: "Sort Order",
      minWidth: 110,
      format: (value) => value,
    },
    {
      id: "status",
      label: "Status",
      minWidth: 100,
      align: "center",
      format: (value) => <StatusChip active={value === true} />,
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 120,
      align: "right",
      format: (_value, row) => (
        <ActionIcons
          actions={[
            {
              icon: <EditIcon />,
              tooltip: "Edit",
              onClick: () => navigate(`/admin/achievements/${row.id}`),
            },
            {
              icon: <DeleteIcon />,
              isDelete: true,
              tooltip: "Delete",
              onClick: () =>
                setDeleteDialog({
                  open: true,
                  id: row.id,
                  title: row?.title,
                }),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Achievements"
        showSearch
        searchPlaceholder="Search achievements..."
        onSearchChange={(value) => setSearch(value)}
        defaultSearchValue={search}
        primaryButton={{
          label: "Add Achievement",
          icon: <AddIcon />,
          onClick: () => {
            navigate("/admin/achievements/create");
          },
        }}
      />
      <PageWrapper loading={loading}>
        <DataTable
          columns={columns}
          rows={achievements}
          totalCount={totalCount}
          onPaginationChange={handlePaginationChange}
          page={page}
          limit={limit}
          tableName="achievements"
          sortField="sort_order"
        />
        <DeleteConfirmation
          open={deleteDialog.open}
          title={"Delete Achievement - " + deleteDialog?.title}
          message="Are you sure you want to delete this achievement? This action cannot be undone."
          apiEndpoint={"achievements/"}
          deleteId={deleteDialog.id}
          onCancel={() => setDeleteDialog({ open: false, id: 0, title: null })}
          refetchApi={() => {
            setPage(0);
            getAchievements();
          }}
        />
      </PageWrapper>
    </>
  );
}
